/**
 * The portal's small component kit. Everything is a thin wrapper over a
 * native element so keyboard and screen-reader behaviour come from the
 * platform: <dialog> for modals (focus trap + Esc), real <input type=radio>
 * inside the decision cards, role="switch" buttons for toggles.
 */
import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ApiError, FieldIssue } from '../api/errors';
import { useNow } from './clock';

// ── Field ────────────────────────────────────────────────────────────────
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export function Field({
  label,
  hint,
  issues = [],
  children,
  optional,
}: {
  label: ReactNode;
  hint?: ReactNode;
  issues?: FieldIssue[];
  optional?: boolean;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = issues.length ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optional ? <span className="faint" style={{ fontWeight: 500 }}> · optional</span> : null}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': issues.length > 0 || undefined })}
      {hint ? (
        <p className="hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {issues.length ? (
        <div className="err" id={errId} role="alert">
          <span aria-hidden="true">●</span>
          <span>{issues.map((i) => i.message).join(' ')}</span>
        </div>
      ) : null}
    </div>
  );
}

// ── Switch ───────────────────────────────────────────────────────────────
export function Switch({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="switch">
      <div className="stack-sm" style={{ gap: 2 }}>
        <span id={`${id}-l`} style={{ fontWeight: 600, color: 'var(--ink)' }}>
          {label}
        </span>
        {description ? (
          <span id={`${id}-d`} className="small muted">
            {description}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-l`}
        aria-describedby={description ? `${id}-d` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}

// ── Decision cards (radio group) ─────────────────────────────────────────
export interface ChoiceOption<T extends string> {
  value: T;
  title: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export function Choices<T extends string>({
  legend,
  value,
  options,
  onChange,
  columns = 1,
  hideLegend,
}: {
  legend: string;
  value: T;
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
  columns?: 1 | 2;
  hideLegend?: boolean;
}) {
  const name = useId();
  return (
    // Each radio is named by its visible title and described by its
    // description, explicitly: inside a wrapping label alone, some assistive
    // tech announced the raw value ("finderly_app"; second audit, 2026-09-27).
    <fieldset>
      <legend className={hideLegend ? 'visually-hidden' : 'label'} style={hideLegend ? undefined : { marginBottom: 8 }}>
        {legend}
      </legend>
      <div className={`choices${columns === 2 ? ' cols-2' : ''}`}>
        {options.map((opt, i) => (
          <label className="choice" key={opt.value} htmlFor={`${name}-${i}`}>
            <input
              id={`${name}-${i}`}
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              disabled={opt.disabled}
              onChange={() => onChange(opt.value)}
              aria-labelledby={`${name}-${i}-t`}
              aria-describedby={opt.description ? `${name}-${i}-d` : undefined}
            />
            <span className="dot" aria-hidden="true" />
            <span>
              <span className="t" id={`${name}-${i}-t`}>
                {opt.title}
              </span>
              {opt.description ? (
                <span className="d" id={`${name}-${i}-d`} style={{ display: 'block' }}>
                  {opt.description}
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────
/**
 * WAI-ARIA tabs with automatic activation: Left/Right (and Home/End) move
 * between tabs and select them, only the selected tab is in the Tab order.
 * Every panel stays mounted (inactive ones `hidden`), so switching tabs never
 * throws away what a panel holds — a key shown once, a half-filled form.
 */
export function Tabs<T extends string>({
  label,
  value,
  onChange,
  tabs,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  tabs: ReadonlyArray<{ value: T; label: ReactNode; panel: ReactNode }>;
}) {
  const base = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (to: number) => {
    const n = tabs.length;
    const i = ((to % n) + n) % n;
    const t = tabs[i];
    if (!t) return;
    onChange(t.value);
    refs.current[i]?.focus();
  };
  const at = tabs.findIndex((t) => t.value === value);
  return (
    <div className="seg-tabs">
      <div role="tablist" aria-label={label} className="seg-tablist">
        {tabs.map((t, i) => (
          <button
            key={t.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${base}-tab-${t.value}`}
            aria-selected={t.value === value}
            aria-controls={`${base}-panel-${t.value}`}
            tabIndex={t.value === value ? 0 : -1}
            onClick={() => onChange(t.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') move(at + 1);
              else if (e.key === 'ArrowLeft') move(at - 1);
              else if (e.key === 'Home') move(0);
              else if (e.key === 'End') move(tabs.length - 1);
              else return;
              e.preventDefault();
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tabs.map((t) => (
        <div
          key={t.value}
          role="tabpanel"
          id={`${base}-panel-${t.value}`}
          aria-labelledby={`${base}-tab-${t.value}`}
          hidden={t.value !== value}
          className="tabpanel"
        >
          {t.panel}
        </div>
      ))}
    </div>
  );
}

// ── Code block with copy ─────────────────────────────────────────────────
export function CopyButton({
  text,
  label = 'Copy',
  className = 'btn btn-sm',
  describedBy,
}: {
  text: string;
  label?: string;
  className?: string;
  /** The caption of what is copied, so several Copy buttons on a page are told apart. */
  describedBy?: string;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={className}
      aria-describedby={describedBy}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        } catch {
          setCopied(false);
        }
      }}
    >
      <span aria-live="polite">{copied ? 'Copied' : label}</span>
    </button>
  );
}

/** Longest piece kept on one line; longer ones (a key, a long URL segment) may break anywhere. */
const CODE_KEEP = 28;

/**
 * One line of code that wraps at sensible points: at spaces, after `/` and
 * after `="`, but not at the hyphens inside `site-key` or
 * `finderly-diagnosis-widget` (short pieces are kept whole).
 */
function CodeLine({ line }: { line: string }) {
  return (
    <span className="ln">
      {line.split(/( +)/).map((token, i) =>
        token.trim() === '' ? (
          token
        ) : (
          <Fragment key={i}>
            {token.split(/(?<=\/|=")/).map((piece, j) => (
              <Fragment key={j}>
                {j > 0 ? <wbr /> : null}
                {piece.length <= CODE_KEEP ? <span className="nw">{piece}</span> : piece}
              </Fragment>
            ))}
          </Fragment>
        ),
      )}
    </span>
  );
}

/**
 * Code to copy. The header row holds the caption and the Copy button, so the
 * button never sits on top of the code; long lines soft-wrap (at spaces, then
 * after `/`) with a hanging indent instead of scrolling sideways. The copied
 * text is exactly `code`. `wrap` is accepted for older callers; every block
 * wraps now (portal audit 2026-09-27: snippets were clipped at 1280 px and a
 * console line ran to 1760 px on a phone).
 */
export function CodeBlock({
  code,
  caption,
  label,
  copy = true,
}: {
  code: string;
  caption?: string;
  label?: string;
  wrap?: boolean;
  /** false when the screen offers its own, more prominent copy action for this code. */
  copy?: boolean;
}) {
  const capId = useId();
  return (
    <div className="codeblock wrap">
      {!copy && !caption ? null : <div className="cap-row">
        {caption ? (
          <span className="cap" id={capId}>
            {caption}
          </span>
        ) : (
          <span className="cap" />
        )}
        {copy ? <CopyButton text={code} className="btn btn-sm copy" describedBy={caption ? capId : undefined} /> : null}
      </div>}
      <pre tabIndex={0} aria-label={label ?? caption ?? 'Code'}>
        <code>
          {code.split('\n').map((line, i) => (
            <CodeLine key={i} line={line} />
          ))}
        </code>
      </pre>
    </div>
  );
}

// ── Disclosure ───────────────────────────────────────────────────────────
/**
 * A button that shows and hides the content under it (aria-expanded +
 * aria-controls), styled as a control with a chevron so it never reads as
 * prose. The body is mounted on first open and then only hidden, so a form
 * inside keeps what was typed when it is closed and reopened.
 */
export function Disclosure({
  label,
  children,
  className = '',
  defaultOpen = false,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [mounted, setMounted] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <div className={`disclose ${open ? 'is-open' : ''} ${className}`.trim()}>
      <button
        type="button"
        className="disclose-btn"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => {
          setOpen(!open);
          setMounted(true);
        }}
      >
        <span className="chev" aria-hidden="true" />
        <span>{label}</span>
      </button>
      <div id={bodyId} className="disclose-body" hidden={!open}>
        {mounted ? children : null}
      </div>
    </div>
  );
}

// ── Dialog ───────────────────────────────────────────────────────────────
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  dismissible = true,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** A one-time secret must not be dismissed by Esc or a stray click. */
  dismissible?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={titleId}
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
    >
      {open ? (
        <>
          <div className="m-head">
            <h2 id={titleId}>{title}</h2>
          </div>
          <div className="m-body">{children}</div>
          {footer ? <div className="m-foot">{footer}</div> : null}
        </>
      ) : null}
    </dialog>
  );
}

// ── Errors ───────────────────────────────────────────────────────────────
export function ErrorNotice({ error, title }: { error: ApiError | null; title?: string }) {
  if (!error) return null;
  return (
    <div className="notice bad" role="alert">
      <div className="stack-sm" style={{ gap: 2 }}>
        <strong>{title ?? error.message}</strong>
        {title ? <span>{error.message}</span> : null}
        {error.fields.length && error.code !== 'VALIDATION_FAILED' ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {error.fields.map((f, i) => (
              <li key={i}>{f.message}</li>
            ))}
          </ul>
        ) : null}
        {error.correlationId ? <span className="corr">Reference {error.correlationId}</span> : null}
      </div>
    </div>
  );
}

// ── Toasts ───────────────────────────────────────────────────────────────
interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'bad';
}
const ToastContext = createContext<(text: string, tone?: 'ok' | 'bad') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: 'ok' | 'bad' = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone === 'bad' ? ' bad' : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export const useToast = () => useContext(ToastContext);

// ── Page header ──────────────────────────────────────────────────────────
export function PageHead({
  title,
  lede,
  eyebrow,
  actions,
}: {
  title: ReactNode;
  lede?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
      <div className="row-between">
        <h1 id="page-title" tabIndex={-1}>
          {title}
        </h1>
        {actions ? <div className="row">{actions}</div> : null}
      </div>
      {lede ? <p className="lede">{lede}</p> : null}
    </header>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite">
      <span className="muted">{label}…</span>
    </div>
  );
}

/** `level` 2 when the empty state sits directly under the page's h1 (no panel heading between). */
export function Empty({ title, children, level = 3 }: { title: string; children?: ReactNode; level?: 2 | 3 }) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <div className="empty">
      <H className="empty-title">{title}</H>
      {children ? <div className="muted" style={{ maxWidth: '48ch' }}>{children}</div> : null}
    </div>
  );
}

// ── Formatting ───────────────────────────────────────────────────────────
export function formatWhen(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = now - d.getTime();
  const abs = Math.abs(diff);
  const fmt = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60_000) return 'just now';
  if (abs < 3_600_000) return fmt.format(-Math.round(diff / 60_000), 'minute');
  if (abs < 86_400_000) return fmt.format(-Math.round(diff / 3_600_000), 'hour');
  if (abs < 7 * 86_400_000) return fmt.format(-Math.round(diff / 86_400_000), 'day');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A calendar date ("Sep 28, 2026"), for when the time of day doesn't matter. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        ...(opts.seconds ? { second: '2-digit' as const } : {}),
      });
}

export function humanize(slug: string | null | undefined): string {
  if (!slug) return '—';
  const s = slug.replace(/[._]/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A relative time on the shared clock (./clock.tsx), with the exact time as its tooltip. */
export function Time({ iso }: { iso: string | null | undefined }) {
  const now = useNow();
  return iso ? (
    <time dateTime={iso} title={formatDateTime(iso)}>
      {formatWhen(iso, now)}
    </time>
  ) : (
    <NotSet />
  );
}

/** The one "not set" mark: a quiet dash that a screen reader reads as "not set". */
export function NotSet({ label = 'Not set' }: { label?: string }) {
  return (
    <span className="faint not-set" title={label}>
      <span aria-hidden="true">—</span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}
